import {
  Account,
  Comment,
  EmailAccount,
  EmailInternetAddress,
} from '@lib/graphql/__generated__/graphql'
import { formatCommentText, handleInlineFilesUpload } from './Comment'
import { formatDate } from './Date'

export const SIGNATURE_PREFIX = 'signature_'

export const formatSignature = (signature: string) => {
  return `<p><br /><span/>${signature}<br /></p>`
}

const getEmailEnvelopeFrom = (comment: Comment) => {
  const { inboundEmailEnvelope, outboundEmailEnvelope } = comment || {}

  return {
    name:
      inboundEmailEnvelope?.from?.name ||
      outboundEmailEnvelope?.from?.name ||
      comment.user?.fullName,
    email:
      inboundEmailEnvelope?.from?.email ||
      outboundEmailEnvelope?.from?.email ||
      comment.user?.email,
  }
}

export const formatDisplayedEmail = (
  email: string,
  name: string,
  useSpecialCharacters: boolean = false,
) => {
  const lt = useSpecialCharacters ? '&lt;' : '<'
  const gt = useSpecialCharacters ? '&gt;' : '>'
  if (name) {
    return `${name} ${lt}${email}${gt}`
  }

  return `${lt}${email}${gt}`
}

export const formatEmailAddresses = (
  emailAddresses: EmailInternetAddress[],
  useSpecialCharacters: boolean = false,
) =>
  emailAddresses
    ?.map((emailAddress) =>
      formatDisplayedEmail(
        emailAddress.email,
        emailAddress.name,
        useSpecialCharacters,
      ),
    )
    ?.join(', ')

export const getCommentEmailReplyTo = (
  comment: Comment,
  emailAccounts: EmailAccount[],
) => {
  const { inboundEmailEnvelope, outboundEmailEnvelope, user } = comment
  const emailEnvelope = inboundEmailEnvelope || outboundEmailEnvelope

  const isReplyingToOwnMessage = emailAccounts.some(
    (emailAccount) => emailAccount.email === emailEnvelope.from?.email,
  )

  if (isReplyingToOwnMessage) {
    return emailEnvelope.to?.map((to) => ({
      name: '',
      email: to.email,
    }))
  }

  return [
    inboundEmailEnvelope?.from ||
      outboundEmailEnvelope?.from || {
        name: '',
        email: user?.email,
      },
  ]
}

export const getCommentEmailReplyCC = (
  comment: Comment,
  emailAccounts: EmailAccount[],
  to: EmailInternetAddress[],
): EmailInternetAddress[] => {
  const { inboundEmailEnvelope, outboundEmailEnvelope } = comment

  const emailEnvelope = inboundEmailEnvelope || outboundEmailEnvelope
  if (!emailEnvelope) return []

  return [...(emailEnvelope.to ?? []), ...(emailEnvelope.cc ?? [])].filter(
    (recipient) => {
      const isNotCurrentUser = emailAccounts.every(
        (account) => account.email !== recipient.email,
      )

      const isNotInToList = to.every(
        (toRecipient) => toRecipient.email !== recipient.email,
      )

      return isNotCurrentUser && isNotInToList
    },
  )
}

export const getCommentEmailReplySubject = (comment: Comment) => {
  const { inboundEmailEnvelope, outboundEmailEnvelope } = comment

  return `${getSubjectPrefix('Re:', comment)}${
    inboundEmailEnvelope?.subject ||
    outboundEmailEnvelope?.subject ||
    comment.emailSubject
  }`
}

export const getCommentEmailForwardSubject = (comment: Comment) => {
  const { inboundEmailEnvelope, outboundEmailEnvelope } = comment
  return `${getSubjectPrefix('FWD:', comment)}${
    inboundEmailEnvelope?.subject ||
    outboundEmailEnvelope?.subject ||
    comment.emailSubject
  }`
}

const getSubjectPrefix = (prefix: string, comment: Comment) =>
  comment.inboundEmailEnvelope?.subject?.startsWith(prefix) ||
  comment.outboundEmailEnvelope?.subject?.startsWith(prefix)
    ? ''
    : prefix + ' '

const prepareCommentResourcesForEmail = async (
  comment: Comment,
  { getPostPolicy },
) => {
  const inlineResources = comment.resources?.filter((r) => r.isInline) ?? []
  const fileObjects = await Promise.all(
    inlineResources?.map((resource) =>
      fetch(resource.presignedUrl)
        .then((response) => response.blob())
        .then((blob) => ({
          id: crypto.randomUUID(),
          file: new File([blob], resource.name, {
            type: resource.contentType,
          }),
        })),
    ),
  )

  const files = await handleInlineFilesUpload({
    inlineFiles: fileObjects,
    getPostPolicy,
  })

  const regexImgElement = new RegExp(`<img.*?>`, 'gm')

  let replacedText = comment.text

  inlineResources.forEach((resource, index) => {
    const regexKeyPattern = new RegExp('{{ ' + resource.key + ' }}', 'gm')

    replacedText = replacedText.replace(regexImgElement, (imageMatch) => {
      if (imageMatch.match(regexKeyPattern)) {
        return imageMatch.replace('<img', `<img id="${files[index].id}"`)
      }
      return imageMatch
    })
  })

  return {
    files,
    text: replacedText,
  }
}

export const getCommentEmailReplyBody = async (
  comment: Comment,
  signature: string,
  { getPostPolicy }: { getPostPolicy: Function },
) => {
  const from = getEmailEnvelopeFrom(comment)

  const { files, text } = await prepareCommentResourcesForEmail(comment, {
    getPostPolicy,
  })

  const emailBody = `
  <p><br>On ${formatDate(comment.createdAt, true)} ${formatDisplayedEmail(
    from.email,
    from.name,
    true,
  )} wrote: </p><p><br /></p>
  ${formatCommentText({
    ...comment,
    text,
    inboundEmailEnvelope: null,
    outboundEmailEnvelope: null,
  })}
  ${signature?.length ? formatSignature(signature) : ''}
  `

  return {
    body: emailBody,
    files,
  }
}

export const getCommentEmailForwardBody = async (
  comment: Comment,
  signature: string,
  { getPostPolicy }: { getPostPolicy: Function },
) => {
  const { inboundEmailEnvelope, outboundEmailEnvelope } = comment
  const from = getEmailEnvelopeFrom(comment)

  const { files, text } = await prepareCommentResourcesForEmail(comment, {
    getPostPolicy,
  })

  const to = formatEmailAddresses(
    (inboundEmailEnvelope || outboundEmailEnvelope)?.to ?? [],
    true,
  )
  const cc = formatEmailAddresses(
    (inboundEmailEnvelope || outboundEmailEnvelope)?.cc ?? [],
    true,
  )
  const subject =
    (inboundEmailEnvelope || outboundEmailEnvelope)?.subject ||
    comment.emailSubject

  const emailBody = `
  <p><br> ------Forwarded message------
  <br>From: ${formatDisplayedEmail(from.email, from.name, true)}
  <br>Date and time: ${formatDate(comment.createdAt, true)}
  <br>Subject: ${subject}
  ${to.length ? `<br>To: ${to}` : ''}
  ${cc.length ? `<br>CC: ${to}` : ''}
  <p><br></p>
  ${formatCommentText({
    ...comment,
    text,
    inboundEmailEnvelope: null,
    outboundEmailEnvelope: null,
  })}
  ${signature?.length ? formatSignature(signature) : ''}
  `

  return {
    body: emailBody,
    files,
  }
}

export const makeEmailAccountsSorter =
  (userAccount: Account) => (a: EmailAccount, b: EmailAccount) => {
    if (!a?.id && b?.id) return 1
    if (a?.id && !b?.id) return -1

    if (a?.account?.id === userAccount?.id && b.account?.id !== userAccount?.id)
      return -1
    if (a?.account?.id !== userAccount?.id && b.account?.id === userAccount?.id)
      return 1

    return a?.email?.localeCompare?.(b?.email) || 0
  }

const signatureBase64ToFile = (base64: string, filename: string) => {
  const arr = base64.split(',')
  const mime = arr[0].match(/:(.*?);/)?.[1] || ''
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  return new File([u8arr], filename, { type: mime })
}

export const emailSignaturesToFiles = (
  html: string,
): { id: string; file: File }[] => {
  const idPattern = /id="([^"]+)"/
  const srcPattern = /src="(data:image\/[^;]+;base64[^"]+)"/

  const matches: { id: string; src: string }[] = []

  const imageTags = html.match(imageTagPattern) || []
  for (const tag of imageTags) {
    const idMatch = idPattern.exec(tag)
    const srcMatch = srcPattern.exec(tag)
    if (idMatch && srcMatch) {
      matches.push({ id: idMatch[1], src: srcMatch[1] })
    }
  }

  return (
    matches.map(({ id, src }, i) => {
      return {
        id,
        file: signatureBase64ToFile(src, `signature_image${i}.png`),
      }
    }) ?? []
  )
}

export const signatureHtmlHasImage = (html: string) => {
  const matches = html.match(imageTagPattern)
  return matches.length
}

const imageTagPattern = /<img\b[^>]*>/g
